// Pure helpers for train seat availability. No imports, so both the Redux slice
// and the availability queue can use them without a circular import.

export const QUOTA_CODES = ['GN', 'TQ', 'PT', 'SS', 'LD', 'DF', 'FT'];

const norm = (s) => String(s || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

export const compareClasses = (c1, c2) => {
    if (!c1 || !c2) return false;
    return norm(c1) === norm(c2);
};

export const isQuotaCode = (cls) => QUOTA_CODES.includes(String(cls || '').toUpperCase());

// Same parsing the cards used for the manual refresh: handles timestamps,
// DD-MM-YYYY, YYYY-MM-DD and ISO strings (date part only, no timezone shift).
export const parseTravelDate = (dStr) => {
    if (!dStr) return null;
    if (typeof dStr === 'number' && !isNaN(dStr)) return new Date(dStr);
    if (typeof dStr === 'string' && /^\d+$/.test(dStr)) return new Date(Number(dStr));

    const parts = String(dStr).split(/[-/\s]/);
    if (parts.length >= 3) {
        const p0 = parseInt(parts[0], 10);
        const p1 = parseInt(parts[1], 10);
        const p2 = parseInt(parts[2], 10);
        if (p2 > 2000 && p1 >= 1 && p1 <= 12 && p0 >= 1 && p0 <= 31) return new Date(p2, p1 - 1, p0);
        if (p0 > 2000 && p1 >= 1 && p1 <= 12 && p2 >= 1 && p2 <= 31) return new Date(p0, p1 - 1, p2);
    }

    const d = new Date(dStr);
    return (!isNaN(d.getTime()) && d.getFullYear() > 2000) ? d : null;
};

const pad2 = (n) => String(n).padStart(2, '0');
const dateKeyCache = new Map();

// DD-MM-YYYY, cached because the reducer computes keys for every segment on every flush
export const toDDMMYYYY = (dt) => {
    if (!dt) return '';
    const k = String(dt);
    const hit = dateKeyCache.get(k);
    if (hit !== undefined) return hit;
    const d = parseTravelDate(dt);
    const s = d ? `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}` : '';
    if (dateKeyCache.size > 5000) dateKeyCache.clear();
    dateKeyCache.set(k, s);
    return s;
};

export const quotaOf = (seg) => {
    const c = seg?.category;
    return (c && c !== 'Reserved' && c !== '(Reserved)') ? c : 'GN';
};

export const segDateOf = (seg) => seg?.origin_dep_dt || seg?.origin?.dep_dt || seg?.departure_date || null;

// A raw backend train segment (not a card/leg view model)
export const isTrainSegment = (seg) => !!seg && !!seg.train_num && !seg.flight_num && !seg.bus_type;

// Identifies one train journey leg. Same segment in different routes => same key,
// so one GET fills it everywhere.
export const segKeyOf = (seg) => {
    if (!seg?.train_num) return null;
    const date = toDDMMYYYY(segDateOf(seg));
    if (!date) return null;
    return `${seg.train_num}|${seg.origin_code || ''}|${seg.dest_code || ''}|${date}|${quotaOf(seg)}`;
};

// Identifies one (segment, seat class) availability call
export const reqKeyOf = (seg, cls) => {
    const k = segKeyOf(seg);
    return k && cls ? `${k}|${norm(cls)}` : null;
};

export const seatClassesOf = (seg) => (seg?.seat_classes || []).filter((c) => c && c !== 'GN');

// The class the route is currently priced and sorted on (what the card shows)
export const primaryClassOf = (seg) => {
    const classes = seatClassesOf(seg);
    const ui = seg?.seat_class_ui || seg?.seat_avail_ui?.seat_class;
    const hit = ui && classes.find((c) => compareClasses(c, ui));
    return hit || classes[0] || ui || null;
};

// ── Class selection ──────────────────────────────────────────────────────
// Highest to lowest. A train's actual classes come from its segment.seat_classes.
export const CLASS_ORDER = ['1A', 'EC', 'EV', '2A', 'CC', '3A', 'SL', '2S'];

const classRank = (cls) => {
    const i = CLASS_ORDER.findIndex((c) => compareClasses(c, cls));
    return i === -1 ? CLASS_ORDER.length : i;
};

// The train's real seat classes (no quota codes), highest first
export const trainClassesOf = (seg) => {
    const seen = new Set();
    return seatClassesOf(seg)
        .filter((c) => {
            if (isQuotaCode(c)) return false;
            const k = norm(c);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        })
        .map((c, i) => ({ c, i, r: classRank(c) }))
        .sort((a, b) => a.r - b.r || a.i - b.i)
        .map((x) => x.c);
};

// The ONE class to check for a train segment (this list is exactly what gets
// fetched, so it always has at most one entry = one call per segment):
// the preferred class if the train has it, else the nearest class below it,
// else the nearest class above it.
// e.g. pref 3A on a train with every class -> 3A
//      pref 3A on a train with 2A, SL, 2S -> SL
//      pref SL on a train with 1A, 2A, 3A -> 3A
export const classPriorityOf = (seg, pref) => {
    const classes = trainClassesOf(seg); // highest first
    if (classes.length === 0) return [];
    const p = pref || seg?.seat_class_ui || seg?.seat_avail_ui?.seat_class || classes[0];
    const pIdx = classes.findIndex((c) => compareClasses(c, p));
    if (pIdx !== -1) return [classes[pIdx]];

    const pRank = classRank(p);
    const below = classes.find((c) => classRank(c) > pRank);
    if (below) return [below];
    const above = classes.filter((c) => classRank(c) < pRank);
    return above.length ? [above[above.length - 1]] : [];
};

// Class to show: the single class picked by classPriorityOf (preferred, else
// nearest below, else nearest above), whether or not it has seats.
export const resolveDisplayClass = (seg, pref, availByClass) => {
    const order = classPriorityOf(seg, pref);
    if (order.length === 0) return null;
    for (const c of order) {
        const e = availEntryOf(availByClass, c);
        if (e && e.is_avail === true) return c;
    }
    return order[0];
};

// Fetched result for one class, written onto the segment by the reducer
export const availEntryOf = (availByClass, cls) => {
    if (!availByClass || !cls) return null;
    if (availByClass[cls]) return availByClass[cls];
    const k = Object.keys(availByClass).find((c) => compareClasses(c, cls));
    return k ? availByClass[k] : null;
};

export const toFareNum = (v) => {
    if (v === null || v === undefined || v === '' || v === '--') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
};

export const isBusyStatus = (s) => s === 'queued' || s === 'loading';

// Stable identity for a route across availability updates (route objects are
// replaced on every update, so object identity can't be used)
export const routeKeyOf = (route) => {
    const sig = (route?.nodes || []).map((n) => {
        const s = n?.segment || {};
        return [
            s.train_num || s.flight_num || s.service_name || s.travel_name || '',
            s.origin_code || s.origin_name || '',
            s.dest_code || s.dest_name || '',
            s.origin_dep_dt || '',
            s.result_idx ?? '',
        ].join('~');
    }).join('>');
    return sig || `rt-${route?.route_type ?? 'x'}`;
};

// Keys for a list, with #2, #3... added for identical routes
export const routeKeysOf = (routes) => {
    const seen = {};
    return (routes || []).map((r) => {
        const k = routeKeyOf(r);
        seen[k] = (seen[k] || 0) + 1;
        return seen[k] > 1 ? `${k}#${seen[k]}` : k;
    });
};
